import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShieldCheck, BadgeCheck, Leaf, Clock } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import About from "./About";
import FloatingActions from "./FloatingActions";
import CookieBanner from "./CookieBanner";
import { contactInfo } from "../mock";

const useReveal = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

const values = [
  { icon: ShieldCheck, title: "Zuverlässig & versichert", text: "Termintreue, feste Ansprechpartner und volle Haftpflichtversicherung – auf uns können Sie sich verlassen." },
  { icon: BadgeCheck, title: "Geschultes Fachpersonal", text: "Unser Team wird regelmäßig geschult und arbeitet nach klaren Qualitäts- und Hygienestandards." },
  { icon: Leaf, title: "Umweltbewusst", text: "Wir setzen auf materialschonende, umweltfreundliche Reinigungsmittel und nachhaltige Verfahren." },
  { icon: Clock, title: "Flexibel für Sie da", text: "Auf Wunsch reinigen wir außerhalb Ihrer Geschäftszeiten – auch früh morgens, abends oder am Wochenende." },
];

const AboutPage = () => {
  useReveal();

  useEffect(() => {
    document.title = "Über uns | MAG Facility Services UG – Gebäudereinigung Augsburg";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", "Über MAG Facility Services UG: Ihre erfahrene Gebäudereinigung aus Augsburg. Zuverlässig, versichert & mit geschultem Team. Lernen Sie unser Unternehmen kennen.");
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", "https://www.mag-facilityservices.de/ueber-uns");
    return () => {
      document.title = "MAG Facility Services UG | Gebäudereinigung Augsburg";
      if (canonical) canonical.setAttribute("href", "https://www.mag-facilityservices.de/");
    };
  }, []);

  return (
    <>
      <Header />
      <main>
        {/* Intro */}
        <section className="relative pt-[74px] bg-white overflow-hidden">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -top-40 right-0 w-[720px] h-[720px] rounded-full bg-blue-100/60 blur-3xl" />
          </div>
          <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-14 lg:py-20">
            <Link to="/" className="inline-flex items-center gap-2 text-[14px] font-medium text-slate-600 hover:text-blue-600 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Zur Startseite
            </Link>
            <span className="mt-8 block text-[13px] font-bold tracking-[0.22em] text-blue-600 uppercase">
              Über uns
            </span>
            <h1 className="font-display mt-3 text-slate-900 text-[34px] sm:text-[50px] leading-[1.05] tracking-tight" style={{ fontWeight: 800 }}>
              Ihre Gebäudereinigung aus Augsburg
            </h1>
            <p className="mt-4 text-[17px] text-slate-600 max-w-2xl leading-relaxed">
              MAG Facility Services UG steht für professionelle, zuverlässige Reinigung in Augsburg und Umgebung. Wir betreuen Büros, Praxen, Wohnanlagen und Gewerbeobjekte mit einem geschulten Team, festen Ansprechpartnern und höchstem Anspruch an Sauberkeit und Hygiene.
            </p>
          </div>
        </section>

        {/* Reused About section (Stats, Features) */}
        <About />

        {/* Werte */}
        <section className="py-16 lg:py-24 bg-slate-50">
          <div className="max-w-6xl mx-auto px-5 sm:px-8">
            <div className="reveal max-w-2xl">
              <span className="text-[13px] font-bold tracking-[0.22em] text-blue-600 uppercase">Unsere Werte</span>
              <h2 className="font-display mt-3 text-slate-900 text-[28px] sm:text-[38px] tracking-tight leading-[1.1]" style={{ fontWeight: 800 }}>
                Wofür wir stehen
              </h2>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              {values.map((v) => (
                <div key={v.title} className="reveal flex gap-4 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-600 shrink-0">
                    <v.icon className="w-6 h-6" strokeWidth={2.1} />
                  </div>
                  <div>
                    <h3 className="text-[17px] font-bold text-slate-900">{v.title}</h3>
                    <p className="mt-1.5 text-[14px] text-slate-600 leading-relaxed">{v.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-5 sm:px-8">
            <div className="rounded-3xl bg-slate-900 px-8 py-12 lg:px-14 text-center">
              <h2 className="font-display text-white text-[26px] sm:text-[34px]" style={{ fontWeight: 800 }}>
                Lernen Sie uns kennen
              </h2>
              <p className="mt-3 text-slate-300 text-[16px] max-w-xl mx-auto">
                Fordern Sie ein kostenloses, unverbindliches Angebot an – wir melden uns innerhalb von 24 Stunden.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <a href="/#kontakt" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors">
                  Kostenloses Angebot anfordern <ArrowRight className="w-4 h-4" />
                </a>
                <a href={contactInfo.phoneHref} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-white/10 text-white font-semibold hover:bg-white/20 transition-colors">
                  {contactInfo.phone}
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingActions />
      <CookieBanner />
    </>
  );
};

export default AboutPage;
